import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { poli } from './poliApi';

/** P-11 Companias del modulo, sus proyectos y que companias ve cada usuario cliente. */
const PoliCompanias = () => {
    const toast = useRef(null);
    const [companias, setCompanias] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [edit, setEdit] = useState(null);
    const [proyecto, setProyecto] = useState('');

    const avisar = (severity, detail) => toast.current && toast.current.show({ severity, summary: severity === 'error' ? 'Error' : 'Guardado', detail, life: 3500 });

    const cargar = useCallback(async () => {
        try {
            const [c, u] = await Promise.all([poli('/companias'), poli('/usuarios-cliente')]);
            setCompanias(c);
            setClientes(u);
            return c;
        } catch (e) {
            avisar('error', e.message);
            return [];
        }
    }, []);

    useEffect(() => { cargar(); }, [cargar]);

    const guardarCompania = async () => {
        try {
            const cuerpo = { codigo: (edit.codigo || '').toUpperCase(), nombre: edit.nombre, ruc: edit.ruc, correoContacto: edit.correo_contacto, activo: edit.activo !== false };
            const r = await poli(edit.compania_id ? `/companias/${edit.compania_id}` : '/companias', edit.compania_id ? 'PUT' : 'POST', cuerpo);
            avisar('success', `Compania ${cuerpo.nombre}`);
            const lista = await cargar();
            setEdit({ ...(lista.find((c) => c.compania_id === r.id) || edit) });
        } catch (e) {
            avisar('error', e.message);
        }
    };

    const agregarProyecto = async () => {
        try {
            await poli(`/companias/${edit.compania_id}/proyectos`, 'POST', { nombre: proyecto });
            setProyecto('');
            const lista = await cargar();
            setEdit({ ...(lista.find((c) => c.compania_id === edit.compania_id) || edit) });
        } catch (e) {
            avisar('error', e.message);
        }
    };

    const asignar = async (u, ids) => {
        try {
            await poli(`/usuarios-cliente/${u.usuario_id}/companias`, 'PUT', { companias: ids });
            avisar('success', `Companias de ${u.username}`);
            cargar();
        } catch (e) {
            avisar('error', e.message);
        }
    };

    const opciones = companias.filter((c) => c.activo).map((c) => ({ label: c.nombre, value: c.compania_id }));

    return (
        <div className="card">
            <Toast ref={toast} />
            <h4 className="mt-0">Companias y usuarios cliente</h4>
            <TabView>
                <TabPanel header="Companias">
                    <div className="flex justify-content-end mb-2">
                        <Button label="Nueva compania" icon="pi pi-plus" onClick={() => setEdit({ activo: true, proyectos: [] })} />
                    </div>
                    <DataTable value={companias} size="small" rowHover onRowClick={(e) => setEdit({ ...e.data })} style={{ cursor: 'pointer' }}
                        emptyMessage="Aun no hay companias" responsiveLayout="scroll">
                        <Column field="codigo" header="Codigo" sortable />
                        <Column field="nombre" header="Nombre" sortable />
                        <Column field="ruc" header="RUC" />
                        <Column header="Proyectos" body={(c) => (c.proyectos || []).filter((p) => p.activo).map((p) => p.nombre).join(', ')} />
                        <Column header="Estado" body={(c) => (c.activo ? <Tag severity="success" value="Activa" /> : <Tag severity="warning" value="Inactiva" />)} />
                    </DataTable>
                </TabPanel>
                <TabPanel header="Usuarios cliente">
                    <p className="text-600 mt-0">Cada usuario con rol de cliente administrativo ve y solicita solo para las companias que tenga asignadas.</p>
                    <DataTable value={clientes} size="small" emptyMessage="Ningun usuario tiene el rol de cliente administrativo" responsiveLayout="scroll">
                        <Column field="username" header="Usuario" />
                        <Column field="nombre" header="Nombre" />
                        <Column header="Companias" body={(u) => (
                            <MultiSelect value={u.companias} options={opciones} display="chip" placeholder="Sin companias" style={{ minWidth: '16rem' }}
                                aria-label={`Companias de ${u.username}`} onChange={(e) => asignar(u, e.value)} />
                        )} />
                    </DataTable>
                </TabPanel>
            </TabView>

            <Dialog header={edit && edit.compania_id ? `Compania ${edit.nombre}` : 'Nueva compania'} visible={!!edit} style={{ width: 'min(34rem, 95vw)' }}
                onHide={() => setEdit(null)} footer={<Button label="Guardar" icon="pi pi-save" onClick={guardarCompania} />}>
                {edit && (
                    <div className="flex flex-column gap-3">
                        <label htmlFor="cia-codigo">Codigo (se usa en las carpetas; sin datos personales)</label>
                        <InputText id="cia-codigo" value={edit.codigo || ''} onChange={(e) => setEdit({ ...edit, codigo: e.target.value.toUpperCase() })} />
                        <label htmlFor="cia-nombre">Nombre</label>
                        <InputText id="cia-nombre" value={edit.nombre || ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} />
                        <label htmlFor="cia-ruc">RUC</label>
                        <InputText id="cia-ruc" value={edit.ruc || ''} keyfilter="int" maxLength={13} onChange={(e) => setEdit({ ...edit, ruc: e.target.value })} />
                        <label htmlFor="cia-correo">Correo de contacto</label>
                        <InputText id="cia-correo" value={edit.correo_contacto || ''} onChange={(e) => setEdit({ ...edit, correo_contacto: e.target.value })} />
                        <div className="flex align-items-center gap-2">
                            <Checkbox inputId="cia-activo" checked={edit.activo !== false} onChange={(e) => setEdit({ ...edit, activo: e.checked })} />
                            <label htmlFor="cia-activo">Activa</label>
                        </div>
                        {edit.compania_id && (
                            <div className="flex flex-column gap-2 border-top-1 surface-border pt-3">
                                <b>Proyectos</b>
                                {(edit.proyectos || []).map((p) => <span key={p.proyecto_id}>{p.nombre}{p.activo ? '' : ' (inactivo)'}</span>)}
                                <div className="p-inputgroup">
                                    <InputText value={proyecto} placeholder="Nuevo proyecto" aria-label="Nuevo proyecto" onChange={(e) => setProyecto(e.target.value)} />
                                    <Button icon="pi pi-plus" aria-label="Agregar proyecto" disabled={!proyecto.trim()} onClick={agregarProyecto} />
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default PoliCompanias;
